import { useEffect, useRef, useState, type ClipboardEvent, type DragEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { POST_MAX, POST_VISIBILITIES, REPLY_POLICIES, type Post, type PostDraft, type PostVisibility, type ReplyPolicy } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import { fullDate } from "../../lib/format";
import { useUI, type ComposerShare } from "../../lib/uiStore";
import { Avatar, CommunityIcon, UserAvatar } from "../../components/ui/Avatar";
import { Button, IconButton } from "../../components/ui/Button";
import { Dialog } from "../../components/ui/Dialog";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Settings";
import { Switch } from "../../components/ui/Controls";
import { Spinner } from "../../components/ui/Spinner";
import { toast } from "../../components/ui/Toast";
import { Tooltip } from "../../components/ui/Tooltip";
import { IconHash, IconImage, IconPlus, IconX } from "../../components/icons";
import { IconDraft, IconMapPoint, IconPollAdd, IconTagUser, IconWarningCW } from "../../components/socialIcons";
import { useMe } from "../auth/AuthProvider";
import { QuoteEmbed } from "./PostCard";
import { useUploads, type PendingUpload } from "./useUploads";
import { MentionTextarea } from "./MentionTextarea";
import { DraftsDialog, PollEditor, SchedulePicker, TagPeopleDialog, emptyPoll, type PendingTag, type PollDraft } from "./ComposerExtras";
import { REPLY_POLICY_LABEL } from "./PostDialogs";
import type { Infinite } from "../../lib/queryClient";
import type { FeedItem, Page } from "@atrium/shared";

function CharRing({ length }: { length: number }) {
  const ratio = Math.min(length / POST_MAX, 1);
  const remaining = POST_MAX - length;
  if (length < POST_MAX * 0.7) return null;
  const r = 9;
  const c = 2 * Math.PI * r;
  return (
    <span className="flex items-center gap-1.5 text-xs tabular" aria-live="polite">
      <svg viewBox="0 0 24 24" className="size-5 -rotate-90">
        <circle cx="12" cy="12" r={r} fill="none" stroke="var(--line-strong)" strokeWidth="2.5" />
        <circle cx="12" cy="12" r={r} fill="none" stroke={remaining < 0 ? "var(--danger)" : remaining < 50 ? "var(--warning)" : "var(--accent)"} strokeWidth="2.5" strokeDasharray={c} strokeDashoffset={c * (1 - ratio)} strokeLinecap="round" />
      </svg>
      <span className={cn(remaining < 0 ? "text-danger" : "text-fg-3")}>{remaining}</span>
    </span>
  );
}

export function UploadPreviews({ items, onRemove, onTag, tags }: { items: PendingUpload[]; onRemove: (key: string) => void; onTag?: (it: PendingUpload) => void; tags?: Record<string, PendingTag[]> }) {
  if (!items.length) return null;
  return (
    <div className="no-scrollbar mt-3 flex gap-2 overflow-x-auto pb-1" data-swipe-ignore>
      {items.map((it) => {
        const n = it.media ? (tags?.[it.media.id]?.length ?? 0) : 0;
        return (
          <div key={it.key} className="relative size-28 shrink-0 overflow-hidden rounded-lg border border-line bg-surface">
            {it.kind === "image" ? <img src={it.preview} alt="" className="h-full w-full object-cover" /> : <video src={it.preview} className="h-full w-full object-cover" muted />}
            {!it.media && !it.error && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/45">
                <div className="h-1 w-16 overflow-hidden rounded-full bg-white/25">
                  <div className="h-full bg-white transition-[width] duration-150" style={{ width: `${Math.round(it.progress * 100)}%` }} />
                </div>
              </div>
            )}
            {it.error && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/70 p-2 text-center text-xs font-medium text-white" role="alert">
                {it.error}
              </div>
            )}
            <button type="button" onClick={() => onRemove(it.key)} aria-label="Remove attachment" className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white hover:bg-black">
              <IconX className="icon size-4" />
            </button>
            {onTag && it.media && it.kind === "image" && (
              <button
                type="button"
                onClick={() => onTag(it)}
                aria-label={n ? `Tagged people: ${n}. Edit tags` : "Tag people"}
                className="absolute bottom-1 left-1 flex h-6 items-center gap-1 rounded-full bg-black/70 px-1.5 text-xs font-medium text-white hover:bg-black"
              >
                <IconTagUser className="size-3.5" aria-hidden />
                {n > 0 && <span className="tabular">{n}</span>}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SharePreview({ share }: { share: ComposerShare }) {
  return (
    <div className="mt-3 rounded-lg border border-line bg-bg-subtle px-3 py-2.5">
      <div className="mb-1.5 flex items-center gap-1 text-xs text-fg-3">
        <span className="font-medium text-fg-2">{share.communityName}</span>
        <IconHash className="icon size-3.5" aria-hidden />
        {share.channelName}
      </div>
      <div className="flex gap-2">
        <Avatar src={share.author.avatarUrl} name={share.author.displayName} size="xs" />
        <div className="min-w-0 text-sm">
          <span className="font-semibold">{share.author.displayName}</span>
          <p className="line-clamp-4 whitespace-pre-wrap text-fg-2">{share.content || "Image"}</p>
        </div>
      </div>
    </div>
  );
}

const AUDIENCE_LABEL: Record<PostVisibility, string> = { public: "Everyone", followers: "Followers", close_friends: "Close friends", mentioned: "Mentioned people" };

interface Payload {
  content: string;
  mediaIds: string[];
  quoteOfId?: string;
  sharedMessageId?: string;
  communityId?: string;
  replyToPostId?: string;
  visibility: PostVisibility;
  replyPolicy: ReplyPolicy;
  contentWarning?: string;
  sensitive: boolean;
  location?: string;
  poll?: { options: string[]; multiple: boolean; durationHours: number };
}

/**
 * The post composer. The common path is just text and a Post button; extras
 * (poll, content warning, location, audience, who can reply, schedule,
 * thread) sit behind icons in the toolbar. Plain posts autosave as a draft
 * while being written; scheduled posts are drafts with a publish time.
 */
export function ComposerForm({
  quoteId,
  share,
  community,
  onDone,
  autoFocus,
  inline,
}: {
  quoteId?: string | null;
  share?: ComposerShare | null;
  community?: { id: string; name: string } | null;
  onDone?: (p: Post) => void;
  autoFocus?: boolean;
  inline?: boolean;
}) {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [thread, setThread] = useState<string[]>([]);
  const [poll, setPoll] = useState<PollDraft | null>(null);
  const [cw, setCw] = useState<string | null>(null);
  const [sensitive, setSensitive] = useState(false);
  const [location, setLocation] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<PostVisibility>("public");
  const [replyPolicy, setReplyPolicy] = useState<ReplyPolicy>("everyone");
  const [scheduledAt, setScheduledAt] = useState<string | null>(null);
  const [restored, setRestored] = useState<string[]>([]);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [tags, setTags] = useState<Record<string, PendingTag[]>>({});
  const [tagging, setTagging] = useState<PendingUpload | null>(null);
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const uploads = useUploads({ max: 10 - restored.length, allowVideo: true });
  const quote = useQuery({ queryKey: ["post", quoteId], queryFn: () => api.get<Post>(`/api/posts/${quoteId}`), enabled: !!quoteId });
  const special = !!share || !!community || !!quoteId;
  const canDraft = !special && thread.length === 0;

  const mediaIds = [...restored, ...uploads.mediaIds];
  const pollValid = !poll || poll.options.filter((o) => o.trim()).length >= 2;
  const empty = !text.trim() && mediaIds.length === 0 && uploads.items.length === 0 && !quoteId && !share && !poll;
  const tooLong = text.length > POST_MAX || thread.some((t) => t.length > POST_MAX);
  const canPost = !empty && !pending && !uploads.uploading && !uploads.failed && !tooLong && pollValid && thread.every((t) => t.trim());

  const payload = (): Payload => ({
    content: text,
    mediaIds,
    quoteOfId: quoteId ?? undefined,
    sharedMessageId: share?.messageId,
    communityId: community?.id,
    visibility: community ? "public" : visibility,
    replyPolicy,
    contentWarning: cw?.trim() || undefined,
    sensitive: sensitive || !!cw?.trim(),
    location: location?.trim() || undefined,
    poll: poll ? { ...poll, options: poll.options.map((o) => o.trim()).filter(Boolean) } : undefined,
  });

  // Autosave plain posts as a draft, a moment after typing stops.
  const latest = useRef(payload);
  latest.current = payload;
  useEffect(() => {
    if (!canDraft || scheduledAt || pending) return;
    if (empty) return;
    const t = setTimeout(async () => {
      try {
        const body = latest.current();
        const d = draftId ? await api.patch<PostDraft>(`/api/drafts/${draftId}`, body) : await api.post<PostDraft>("/api/drafts", body);
        setDraftId(d.id);
        setSavedAt(new Date());
        void qc.invalidateQueries({ queryKey: ["drafts"] });
      } catch {
        /* autosave is best effort */
      }
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, poll, cw, sensitive, location, visibility, replyPolicy, mediaIds.join(","), canDraft, scheduledAt]);

  const reset = () => {
    setText("");
    setThread([]);
    setPoll(null);
    setCw(null);
    setSensitive(false);
    setLocation(null);
    setVisibility("public");
    setReplyPolicy("everyone");
    setScheduledAt(null);
    setRestored([]);
    setDraftId(null);
    setSavedAt(null);
    setTags({});
    uploads.reset();
  };

  const resume = (d: PostDraft) => {
    const p = d.payload as Partial<Payload>;
    setText(p.content ?? "");
    setPoll(p.poll ? { options: p.poll.options, multiple: p.poll.multiple, durationHours: p.poll.durationHours } : null);
    setCw(p.contentWarning ?? null);
    setSensitive(!!p.sensitive);
    setLocation(p.location ?? null);
    setVisibility(p.visibility ?? "public");
    setReplyPolicy(p.replyPolicy ?? "everyone");
    setRestored(p.mediaIds ?? []);
    setThread([]);
    setScheduledAt(null);
    uploads.reset();
    setDraftId(d.id);
  };

  const prepend = (post: Post) => {
    for (const mode of ["latest", "following"]) {
      qc.setQueryData<Infinite<Page<FeedItem>>>(["feed", mode], (old) =>
        old ? { ...old, pages: old.pages.map((p, i) => (i === 0 ? { ...p, items: [{ key: post.id, repostedBy: null, post }, ...p.items] } : p)) } : old,
      );
    }
  };

  const submit = async () => {
    if (!canPost) return;
    setPending(true);
    try {
      if (scheduledAt) {
        const body = { ...payload(), scheduledAt };
        if (draftId) await api.patch(`/api/drafts/${draftId}`, body);
        else await api.post("/api/drafts", body);
        reset();
        void qc.invalidateQueries({ queryKey: ["drafts"] });
        toast.show(`Scheduled for ${fullDate(body.scheduledAt)}`, { label: "View", onClick: () => setDraftsOpen(true) });
        return;
      }
      // A thread publishes in order, each post continuing the one before.
      const first = await api.post<Post>("/api/posts", payload());
      let prev = first;
      for (const [i, part] of thread.entries()) {
        try {
          prev = await api.post<Post>("/api/posts", { content: part, visibility: first.visibility, replyPolicy, replyToPostId: prev.id });
        } catch (e) {
          // Keep what didn't publish so nothing is lost.
          setThread(thread.slice(i));
          setText("");
          toast.error(errorMessage(e, "Part of your thread couldn't be published"));
          prepend(first);
          return;
        }
      }
      if (draftId) void api.del(`/api/drafts/${draftId}`).catch(() => {});
      reset();
      prepend(first);
      void qc.invalidateQueries({ queryKey: ["drafts"] });
      void qc.invalidateQueries({ queryKey: ["profile-posts", me.username] });
      void qc.invalidateQueries({ queryKey: ["profile", me.username] });
      if (quoteId) void qc.invalidateQueries({ queryKey: ["post", quoteId] });
      if (community) void qc.invalidateQueries({ queryKey: ["community-posts"] });
      onDone?.(first);
      if (!inline) toast.show(thread.length ? "Thread posted" : "Posted", { label: "View", onClick: () => navigate(`/p/${first.id}`) });
    } catch (e) {
      toast.error(errorMessage(e, "Couldn't publish your post"));
    } finally {
      setPending(false);
    }
  };

  const onPaste = (e: ClipboardEvent) => {
    const files = Array.from(e.clipboardData.files);
    if (files.length) {
      e.preventDefault();
      uploads.add(files);
    }
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length) uploads.add(e.dataTransfer.files);
  };

  const expanded = !inline || focused || !empty || thread.length > 0;
  const keys = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void submit();
    }
  };
  const label = scheduledAt ? "Schedule" : community ? "Publish" : thread.length ? "Post all" : "Post";

  return (
    <div
      className={cn("flex gap-3", dragging && "rounded-lg outline-2 outline-dashed outline-accent")}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      {community ? <CommunityIcon src={null} name={community.name} size={inline ? "sm" : "md"} /> : <UserAvatar user={{ ...me, decoration: me.profile.decoration, avatarEffect: null }} size={inline ? "sm" : "md"} />}
      <div className="min-w-0 flex-1" onFocus={() => setFocused(true)} onBlur={(e) => !e.currentTarget.contains(e.relatedTarget as Node) && setFocused(false)}>
        {community && <div className="mb-1 text-xs font-medium text-fg-3">Publishing as {community.name}</div>}
        {cw !== null && (
          <div className="mb-2 flex items-center gap-2">
            <IconWarningCW className="icon size-[18px] shrink-0 text-warning" aria-hidden />
            <Input value={cw} autoFocus maxLength={120} onChange={(e) => setCw(e.target.value)} placeholder="Content warning, e.g. spoilers" aria-label="Content warning" className="!h-9" />
            <IconButton size="sm" label="Remove content warning" icon={IconX} onClick={() => setCw(null)} />
          </div>
        )}
        <MentionTextarea
          value={text}
          autoFocus={autoFocus}
          onChange={setText}
          onPaste={onPaste}
          onKeyDown={keys}
          autoGrow={420}
          rows={inline && !expanded ? 1 : 3}
          placeholder={quoteId ? "Add your thoughts" : share ? "Say something about this" : community ? "Write an announcement" : inline ? "Share something with your communities and followers" : "Share something"}
          aria-label="Post text"
          className={cn("w-full", inline ? "pt-1 text-base leading-6" : "pt-1.5 text-lg leading-7")}
        />
        {restored.length > 0 && (
          <div className="mt-3 flex gap-2">
            {restored.map((id) => (
              <div key={id} className="relative size-28 shrink-0 overflow-hidden rounded-lg border border-line bg-surface">
                <img src={`/media/${id}`} alt="" className="h-full w-full object-cover" />
                <button type="button" onClick={() => setRestored((r) => r.filter((x) => x !== id))} aria-label="Remove attachment" className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white hover:bg-black">
                  <IconX className="icon size-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        <UploadPreviews items={uploads.items} onRemove={uploads.remove} onTag={setTagging} tags={tags} />
        {poll && <PollEditor poll={poll} onChange={setPoll} onRemove={() => setPoll(null)} />}
        {quoteId && (quote.data ? <QuoteEmbed post={quote.data} /> : <div className="mt-3 flex h-16 items-center justify-center rounded-lg border border-line"><Spinner className="size-4 text-fg-3" /></div>)}
        {share && <SharePreview share={share} />}
        {location !== null && (
          <div className="mt-2 flex items-center gap-2">
            <IconMapPoint className="icon size-[18px] shrink-0 text-fg-3" aria-hidden />
            <Input value={location} autoFocus maxLength={80} onChange={(e) => setLocation(e.target.value)} placeholder="Add a place" aria-label="Location" className="!h-9" />
            <IconButton size="sm" label="Remove location" icon={IconX} onClick={() => setLocation(null)} />
          </div>
        )}
        {thread.map((t, i) => (
          <div key={i} className="mt-3 border-l-2 border-line pl-3">
            <div className="flex items-start gap-2">
              <MentionTextarea
                value={t}
                autoFocus
                onChange={(v) => setThread((x) => x.map((y, j) => (j === i ? v : y)))}
                onKeyDown={keys}
                autoGrow={240}
                rows={2}
                placeholder="Add to thread"
                aria-label={`Thread post ${i + 2}`}
                className="w-full pt-1 text-base leading-6"
              />
              <IconButton size="sm" label={`Remove thread post ${i + 2}`} icon={IconX} onClick={() => setThread((x) => x.filter((_, j) => j !== i))} />
            </div>
          </div>
        ))}

        <div className={cn("mt-2 pt-1", !inline && "border-t border-line pt-2", !expanded && "hidden")}>
          {(sensitive || cw) && uploads.items.length + restored.length > 0 && (
            <div className="mb-1 text-xs text-fg-3">Media will be marked sensitive.</div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="-ml-2 flex flex-wrap items-center">
              <input
                ref={fileRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,image/gif,image/avif,video/mp4,video/webm,video/quicktime"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) uploads.add(e.target.files);
                  e.target.value = "";
                }}
              />
              <IconButton label="Add photos or video" icon={IconImage} onClick={() => fileRef.current?.click()} disabled={mediaIds.length + uploads.items.length >= 10 || !!poll} />
              {!share && <IconButton label="Add a poll" icon={IconPollAdd} active={!!poll} onClick={() => setPoll(poll ? null : emptyPoll())} disabled={uploads.items.length + restored.length > 0} />}
              <IconButton label="Content warning" icon={IconWarningCW} active={cw !== null} onClick={() => setCw(cw === null ? "" : null)} />
              <IconButton label="Add location" icon={IconMapPoint} active={location !== null} onClick={() => setLocation(location === null ? "" : null)} />
              {!community && <SchedulePicker value={scheduledAt} onChange={setScheduledAt} />}
              {!special && !scheduledAt && <IconButton label="Add to thread" icon={IconPlus} onClick={() => setThread((x) => [...x, ""])} disabled={thread.length >= 9} />}
              {!special && <IconButton label="Drafts" icon={IconDraft} onClick={() => setDraftsOpen(true)} />}
            </div>
            <div className="flex items-center gap-3">
              {savedAt && canDraft && <span className="hidden text-xs text-fg-3 sm:inline">Draft saved</span>}
              <CharRing length={text.length} />
              <Button variant="primary" onClick={() => void submit()} disabled={!canPost} loading={pending}>
                {label}
              </Button>
            </div>
          </div>
          {!community && (
            <div className="-ml-0.5 mt-1.5 flex flex-wrap items-center gap-2 text-sm">
              <Tooltip label="Who can see this">
                <span>
                  <Select<PostVisibility> aria-label="Who can see this" value={visibility} onChange={setVisibility} options={POST_VISIBILITIES.map((v) => ({ value: v, label: AUDIENCE_LABEL[v] }))} />
                </span>
              </Tooltip>
              <Tooltip label="Who can reply">
                <span>
                  <Select<ReplyPolicy> aria-label="Who can reply" value={replyPolicy} onChange={setReplyPolicy} options={REPLY_POLICIES.map((v) => ({ value: v, label: REPLY_POLICY_LABEL[v] }))} />
                </span>
              </Tooltip>
              {uploads.items.length + restored.length > 0 && !cw && (
                <label className="flex items-center gap-2 text-sm text-fg-2">
                  <Switch label="Sensitive media" checked={sensitive} onCheckedChange={setSensitive} />
                  Sensitive
                </label>
              )}
            </div>
          )}
        </div>
      </div>
      {tagging?.media && (
        <TagPeopleDialog
          open={!!tagging}
          onOpenChange={(o) => !o && setTagging(null)}
          image={tagging.preview}
          mediaId={tagging.media.id}
          tags={tags[tagging.media.id] ?? []}
          onSaved={(t) => setTags((all) => ({ ...all, [tagging.media!.id]: t }))}
        />
      )}
      <DraftsDialog open={draftsOpen} onOpenChange={setDraftsOpen} onResume={resume} />
    </div>
  );
}

export function ComposerDialog() {
  const { composerOpen, composerQuoteId, composerShare, composerCommunity, closeComposer } = useUI();
  return (
    <Dialog
      open={composerOpen}
      onOpenChange={(o) => !o && closeComposer()}
      title={composerQuoteId ? "Quote post" : composerShare ? "Share to feed" : composerCommunity ? "New announcement" : "New post"}
      size="lg"
    >
      {composerOpen && <ComposerForm key={`${composerQuoteId}-${composerShare?.messageId}-${composerCommunity?.id}`} quoteId={composerQuoteId} share={composerShare} community={composerCommunity} autoFocus onDone={closeComposer} />}
    </Dialog>
  );
}
